import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { useT } from "@/i18n";
import { AuthForm } from "@/components/auth/AuthForm";
import { useAuth } from "@/hooks/use-auth";

/**
 * Backwards-compatible API: existing callers pass `open`, `onClose`, `reason`.
 * The modal now actually completes the sign-in flow and resumes the gated
 * action via the auth context's `requireSignIn` callback when used through
 * `useAuth().requireSignIn(...)`.
 */
export function SignInModal({
  open,
  onClose,
  reason,
}: {
  open: boolean;
  onClose: () => void;
  reason?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const t = useT();
  const { user } = useAuth();

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const node = containerRef.current;
    const focusable = node?.querySelectorAll<HTMLElement>(
      'a, button, [href], input, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.[0]?.focus();

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === "Tab" && focusable && focusable.length > 0) {
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, [open, onClose]);

  // If sign-in completed elsewhere, close.
  useEffect(() => {
    if (open && user) onClose();
  }, [open, user, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 backdrop-blur-sm p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="signin-modal-title"
        className="w-full max-w-md bg-card border-2 border-foreground shadow-[8px_8px_0_0_hsl(var(--foreground))] outline-none max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        <div className="flex items-center justify-between border-b border-foreground px-5 py-3 sticky top-0 bg-card z-10">
          <span id="signin-modal-title" className="font-mono text-xs uppercase tracking-[0.2em]">
            {reason ?? t("signInModal.defaultReason")}
          </span>
          <button onClick={onClose} aria-label={t("common.close")} className="-mr-1 p-1">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6">
          <AuthForm
            defaultMode="sign-in"
            size="compact"
          />
        </div>
      </div>
    </div>
  );
}
